/**
 * SSO 정보 제공 동의 UI 컴포넌트
 * 
 * 외부 SSO 제공자(KHA 등)에서 메디크루로 정보가 전송될 때
 * 사용자에게 제3자 개인정보 제공 동의를 받는 화면
 */

import React from 'react';
import { Button } from '@/components/shared/ui';
import type { UserSsoAccessDto, SsoOrigin } from '@/types/api/sso';

interface SsoConsentFormProps {
  /** SSO 제공자 (현재는 'kha'만 지원) */
  origin: SsoOrigin;
  /** SSO 접근 정보 */
  userSsoAccess: UserSsoAccessDto;
  /** "동의하고 계속" 버튼 클릭 핸들러 */
  onConsent: () => void;
  /** "취소" 버튼 클릭 핸들러 */
  onCancel: () => void;
  /** 제출 중 여부 (버튼 비활성화용) */
  isSubmitting?: boolean;
}

/**
 * SSO 제공자 정보 매핑
 */
const SSO_PROVIDER_INFO: Record<SsoOrigin, { name: string; logoUrl: string }> = {
  kha: {
    name: '대한병원협회',
    logoUrl: '/assets/images/sso/kha_logo.png',
  },
};

const medicrewLogo = '/assets/images/brand/medicrew_blue_logo.png';

/**
 * 회원등급 표시 함수
 * premium: true → '정회원', false → '준회원'
 */
const getMembershipGrade = (premium: boolean): string => {
  return premium ? '정회원' : '준회원';
};

export default function SsoConsentForm({
  origin,
  userSsoAccess,
  onConsent,
  onCancel,
  isSubmitting = false,
}: SsoConsentFormProps): React.ReactElement {
  const provider = SSO_PROVIDER_INFO[origin];

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 p-6">
      {/* Consent Card */}
      <div className="w-full max-w-[400px] bg-white rounded-2xl p-8 shadow-[0_15px_40px_-10px_rgba(0,0,0,0.08)] border border-gray-100">
        {/* Header: Logos with Arrow */}
        <div className="flex items-center justify-center gap-4 mb-7">
          <img src={provider.logoUrl} alt={provider.name} className="h-8 w-auto" />
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
          </svg>
          <img src={medicrewLogo} alt="Medicrew" className="h-6 w-auto" />
        </div>

        {/* Title */}
        <h1 className="text-xl font-bold text-gray-900 mb-2 text-center">
          제3자 개인정보제공동의 요청
        </h1>

        {/* Description */}
        <p className="text-sm text-gray-600 text-center mb-6">
          아래 정보가 {provider.name}에서 메디크루로 전송됩니다.
          <br />
          동의하시면 계속 진행해 주세요.
        </p>

        {/* Information Table */}
        <div className="bg-gray-50 rounded-xl p-4 mb-6">
          <div className="space-y-3">
            <div className="flex justify-between items-center pb-3 border-b border-gray-200">
              <span className="text-xs font-medium text-gray-600">제공받는 곳</span>
              <span className="text-sm font-semibold text-gray-900">메디크루</span>
            </div>
            <div className="flex justify-between items-center pb-3 border-b border-gray-200">
              <span className="text-xs font-medium text-gray-600">이름</span>
              <span className="text-sm font-semibold text-gray-900">{userSsoAccess.name}</span>
            </div>
            <div className="flex justify-between items-center pb-3 border-b border-gray-200">
              <span className="text-xs font-medium text-gray-600">이메일</span>
              <span className="text-sm font-medium text-gray-700">{userSsoAccess.email}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-xs font-medium text-gray-600">회원등급</span>
              <span className="text-sm font-semibold text-gray-900">
                {getMembershipGrade(userSsoAccess.premium)}
              </span>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="space-y-2">
          <Button
            type="button"
            className="w-full rounded-full text-base h-11"
            variant="primary"
            onClick={onConsent}
            disabled={isSubmitting}
          >
            {isSubmitting ? '처리 중...' : '동의하고 계속'}
          </Button>

          <button
            type="button"
            className="w-full text-gray-500 hover:text-gray-900 transition-colors text-sm py-2"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            취소
          </button>
        </div>
      </div>

      {/* Powered By */}
      <div className="mt-7 flex justify-center items-center gap-2">
        <span className="text-xs text-gray-400">Powered by</span>
        <span className="text-xs font-bold text-gray-500">NEWBASE</span>
      </div>
    </div>
  );
}
